import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import MarketIcon from '../Flags/MarketIcon';

const OPTIONS = [
    { mode: 'brl', label: 'Brasil', currency: 'R$', active: 'bg-emerald-500/20 text-white ring-1 ring-emerald-500/60' },
    { mode: 'usd', label: 'Global', currency: 'US$', active: 'bg-blue-500/20 text-white ring-1 ring-blue-500/60' },
    { mode: 'mxn', label: 'LATAM', currency: 'MX$', active: 'bg-teal-500/20 text-white ring-1 ring-teal-500/60' },
];

const optionOf = (mode) => OPTIONS.find((o) => o.mode === mode);
const labelOf = (mode) => optionOf(mode)?.label;

// Os valores NÃO são convertidos — só muda o que o número passa a representar.
function confirmText(fromMode, toMode) {
    return `Os números não serão convertidos: um valor 100,00 que hoje é ${optionOf(fromMode)?.currency} 100,00 `
        + `passa a ser ${optionOf(toMode)?.currency} 100,00. Revise os valores e as mensagens personalizadas.`;
}

// Por que o mercado está trancado (falta o gateway da moeda dele).
const LOCKED = {
    usd: { title: 'Global indisponível', text: 'O modo Global exige um gateway de cartão conectado (Stripe ou Cooud).', hint: 'Exige um gateway de cartão conectado' },
    mxn: { title: 'LATAM indisponível', text: 'O modo LATAM exige a XPag ou a NXGate conectada com ao menos um país: México (MXN), Colômbia (COP) ou Chile (CLP).', hint: 'Exige a XPag ou a NXGate' },
};

/**
 * Controle segmentado [Brasil | Global | LATAM] — só visual e interação, sem API.
 * LATAM só aparece com `allowLatam` (o bot; o fluxo ainda não tem LATAM).
 * Quem usa decide o que "trocar" significa (bot grava na API; fluxo muda o
 * editor e grava no Salvar).
 *
 * - Trocar pede confirmação inline; `onChange(mode)` só depois de confirmar.
 * - Global sem cartão (`hasCard === false`) mostra cadeado e, no clique,
 *   explica e leva a Gateways.
 * - `subject` entra no texto da confirmação ("Mudar este bot/fluxo para…").
 * - `value` null = moeda ainda não carregada: nenhuma opção marcada (não
 *   chuta Brasil para depois "pular" para Global).
 */
export default function MarketSegmentedControl({ value, onChange, hasCard, hasLatam, allowLatam = false, saving = false, subject = 'bot' }) {
    const options = allowLatam ? OPTIONS : OPTIONS.filter((o) => o.mode !== 'mxn');
    // null = moeda ainda desconhecida (carregando): nada marcado, nada clicável.
    const mode = options.some((o) => o.mode === value) ? value : null;
    const unknown = mode === null;
    // null | { kind: 'confirm', to: 'brl'|'usd'|'mxn' } | { kind: 'locked', to: 'usd'|'mxn' }
    const [popover, setPopover] = useState(null);

    useEffect(() => {
        if (!popover) return;
        const onKey = (e) => { if (e.key === 'Escape') setPopover(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [popover]);

    const locked = (target) => (target === 'usd' && hasCard === false) || (target === 'mxn' && hasLatam === false);

    const pick = (target) => {
        if (saving || unknown || target === mode) return;
        setPopover({ kind: locked(target) ? 'locked' : 'confirm', to: target });
    };

    const confirm = () => {
        const target = popover?.to;
        setPopover(null);
        if (target && target !== mode) onChange?.(target);
    };

    return (
        <div className="relative inline-block">
            <div
                role="radiogroup"
                aria-label="Moeda"
                aria-busy={saving || unknown}
                className={`inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-white/10 bg-white/[0.03] transition-opacity ${
                    unknown ? 'opacity-60 pointer-events-none' : ''
                }`}
            >
                {options.map((o) => {
                    const active = o.mode === mode;
                    const isLocked = locked(o.mode);
                    return (
                        <button
                            key={o.mode}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-disabled={isLocked}
                            title={isLocked ? LOCKED[o.mode].hint : `Cobrar em ${o.currency}`}
                            onClick={() => pick(o.mode)}
                            className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium whitespace-nowrap transition-colors ${
                                active ? o.active : 'text-white/55 hover:text-white hover:bg-white/[0.05]'
                            } ${isLocked ? 'opacity-50' : ''}`}
                        >
                            {saving && active
                                ? <Loader2 size={12} className="animate-spin" />
                                : <MarketIcon mode={o.mode} />}
                            <span>{o.label}</span>
                            {isLocked && <Lock size={11} className="opacity-70" />}
                        </button>
                    );
                })}
            </div>

            {popover && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setPopover(null)} />
                    <div
                        role="dialog"
                        aria-label={popover.kind === 'confirm' ? `Mudar para ${labelOf(popover.to)}` : LOCKED[popover.to].title}
                        className="absolute left-0 top-full mt-1.5 z-50 w-72 rounded-xl border border-white/10 bg-[#10141a] shadow-[0_16px_48px_rgba(0,0,0,0.55)] p-3 text-white"
                    >
                        {popover.kind === 'locked' ? (
                            <>
                                <p className="text-sm font-semibold mb-1">{LOCKED[popover.to].title}</p>
                                <p className="text-[11px] text-white/60 leading-relaxed">
                                    {LOCKED[popover.to].text}{' '}
                                    <Link to="/gateways" className="underline text-amber-300 hover:text-amber-200">Conectar em Gateways</Link>
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-semibold mb-1">Mudar este {subject} para {labelOf(popover.to)}?</p>
                                <p className="text-[11px] text-white/60 leading-relaxed mb-3">{confirmText(mode, popover.to)}</p>
                                <div className="flex justify-end gap-2">
                                    <button type="button" onClick={() => setPopover(null)}
                                        className="h-8 px-3 rounded-lg border border-white/10 text-white/60 hover:text-white hover:bg-white/[0.04] text-xs">
                                        Cancelar
                                    </button>
                                    <button type="button" onClick={confirm}
                                        className="h-8 px-3 rounded-lg text-xs font-medium text-white"
                                        style={{ background: 'linear-gradient(135deg, var(--purple), #7c3aed)' }}>
                                        Mudar para {labelOf(popover.to)}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
